import type { CampaignInstrument, InstrumentCloseReason, InstrumentStatus } from '../types'

/** Figma launch row status pill — label + colors. */
export type InstrumentLaunchStatusPill = {
  label: string
  bg: string
  color: string
}

const LIVE_PILL = { label: '● Live', bg: '#FFF0F0', color: '#FF5959' } as const
const DRAFT_PILL = { label: 'Draft', bg: '#FBF2C6', color: '#F59E0B' } as const
const PAUSE_PILL = { label: 'Paused', bg: '#f7e7cb', color: '#f59e0b' } as const
const STOPPED_PILL = { label: 'Stopped', bg: '#E2E8F0', color: '#475569' } as const
const COMPLETED_PILL = { label: 'Completed', bg: '#DAF5E4', color: '#16A34A' } as const
const EXPIRED_PILL = { label: 'Expired', bg: '#E2E8F0', color: '#475569' } as const
/** Reserved for future manual close — same gray treatment as Expired. */
const CLOSED_PILL = { label: 'Closed', bg: '#E2E8F0', color: '#475569' } as const

/**
 * Sensemaker launching screen labels.
 *
 * Canonical API statuses: `draft | active | paused | completed | expired | closed`.
 * Legacy payloads may still send `closed` + `closeReason` — map those for display.
 *
 * - Draft, Live (active), Pause (paused)
 * - Completed — target reached
 * - Expired — end date before target (gray pill)
 * - Closed — manual end (future)
 */
export function resolveInstrumentLaunchStatusPill(
  status: InstrumentStatus,
  closeReason?: InstrumentCloseReason | null,
): InstrumentLaunchStatusPill {
  switch (status) {
    case 'draft':
      return DRAFT_PILL
    case 'paused':
      return PAUSE_PILL
    case 'stopped':
      return STOPPED_PILL
    case 'completed':
      return COMPLETED_PILL
    case 'expired':
      return EXPIRED_PILL
    case 'closed':
      // Legacy: status stayed `closed` with closeReason until BE migration.
      if (closeReason === 'target_reached') return COMPLETED_PILL
      if (closeReason === 'timeline_expired') return EXPIRED_PILL
      return CLOSED_PILL
    case 'active':
    default:
      return LIVE_PILL
  }
}

/** Whether the response progress bar is shown on the launch row. */
export function shouldShowLaunchResponseProgress(instrument: Pick<
  CampaignInstrument,
  'status' | 'closeReason' | 'responseTarget'
>): boolean {
  const target = Math.max(0, instrument.responseTarget ?? 0)
  if (target <= 0) return false
  if (instrument.status === 'active' || instrument.status === 'paused') return true
  if (instrument.status === 'completed' || instrument.status === 'expired') return true
  // Legacy closed docs
  if (instrument.status === 'closed') {
    return (
      instrument.closeReason === 'target_reached' ||
      instrument.closeReason === 'timeline_expired'
    )
  }
  return false
}
